import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  committee_code: string;
  name: string;
  purpose?: string | null;
  status: string;
  member_count?: number;
};

export default function GovernanceCommitteeListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.governance.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-governance-committees', status, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/governance/committees', {
        params: { status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('governanceCommittees')}
      description={t('governanceCommitteesIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/governance')}>{t('governance')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/governance/committees/new')}>
              {t('newCommittee')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 120 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'active', label: t('statusActive') },
                  { value: 'inactive', label: t('statusInactive') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/governance/committees/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('committeeCode'), dataIndex: 'committee_code', width: 140 },
              { title: t('name'), dataIndex: 'name' },
              { title: t('members'), dataIndex: 'member_count', width: 100 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
